import {
  ArrowSmallBottomIcon,
  ArrowSmallTopIcon,
  Box,
  BreadcrumbItem,
  Breadcrumbs,
  Button,
  Flex,
  Grid,
  MicroButton,
  Stack,
  Text,
  useMediaQuery
} from '@viveaesthetics/shared'
import React, { useState } from 'react'

import { InternalLink } from '@/components/InternalLink/InternalLink'
import {
  getUrlWithPersistedParams,
  THERAPISTS_PAGE_PERSISTED_PARAMS
} from '@/utils/providers'

export interface NavigationLink {
  label: string
  url: string
}

interface NavigationBlockProps {
  breadcrumbLinks: BreadcrumbItem[]
  location: string
  providerCount: number
  showListButton?: boolean
  showListLabel: string
  briefLinksCount?: number
  isExpandedByDefault?: boolean
  linksList: NavigationLink[]
}

export const NavigationBlock = ({
  breadcrumbLinks,
  providerCount,
  location,
  showListButton,
  showListLabel,
  briefLinksCount,
  isExpandedByDefault,
  linksList
}: NavigationBlockProps) => {
  const { isMobile } = useMediaQuery()
  const [listShown, setListShown] = useState(isExpandedByDefault || false)
  const [listFullyShown, setListFullyShown] = useState(
    isExpandedByDefault || false
  )

  const sortedLinksList = linksList.sort((a, b) =>
    a.label.localeCompare(b.label)
  )
  const briefLinksList = sortedLinksList.slice(0, briefLinksCount)
  const visibleLinksList = listFullyShown ? sortedLinksList : briefLinksList

  return (
    <Box $padding={['spacing16', 'spacing0']}>
      <Stack $space="spacing12">
        <Flex
          align="center"
          justify="space-between"
          template={['0 1 56rem', 'none']}
        >
          <Stack $space="spacing8">
            <Breadcrumbs
              config={breadcrumbLinks}
              linkComponent={InternalLink}
            />
            <Text
              type="subtitle1"
              $color="textDefault"
            >
              {providerCount} providers in {location} matching your needs
            </Text>
            {!isMobile && (
              <Text
                type="paragraphS"
                $color="textSecondary"
              >
                Take advantage of your insurance plan. Schedule a session with a
                licensed and trusted therapist that’s in network with your
                insurance.
              </Text>
            )}
          </Stack>
          {showListButton && (
            <MicroButton
              onClick={() => setListShown(prev => !prev)}
              data-test-id="show-list-button"
            >
              {showListLabel}
              {listShown ? <ArrowSmallTopIcon /> : <ArrowSmallBottomIcon />}
            </MicroButton>
          )}
        </Flex>
        <Grid
          /* eslint-disable-next-line react/forbid-component-props */
          style={{ ...(!listShown && { display: 'none' }) }}
          min="17rem"
          $space={['spacing2', 'spacing24']}
        >
          {visibleLinksList.map(link => (
            <Flex key={link.label}>
              <Box onClick={() => setListShown(false)}>
                <Button
                  variant="link"
                  variantSize="small"
                  key={link.label}
                  as={InternalLink}
                  href={getUrlWithPersistedParams(
                    link.url,
                    THERAPISTS_PAGE_PERSISTED_PARAMS
                  )}
                  data-test-id={`link-${link.label}`}
                >
                  {link.label}
                </Button>
              </Box>
            </Flex>
          ))}
        </Grid>
        {listShown && briefLinksList.length < linksList.length && (
          <Flex align="start">
            <Button
              variant="link"
              variantSize="small"
              onClick={() => setListFullyShown(prev => !prev)}
              data-test-id="show-all-links-button"
            >
              Show {listFullyShown ? 'less' : 'more'}
              {listFullyShown ? (
                <ArrowSmallTopIcon />
              ) : (
                <ArrowSmallBottomIcon />
              )}
            </Button>
          </Flex>
        )}
      </Stack>
    </Box>
  )
}
